export const LAST_AUTHENTICATED_ROUTE_STORAGE_KEY =
  "last-authenticated-route";

const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/forgot-workspace",
  "/accept-invitation",
];

type LocationLike = {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
};

export function isAuthRoutePath(pathname: string): boolean {
  return AUTH_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function normalizePathSegment(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeRouteTarget(target: string | null | undefined): string | null {
  if (typeof target !== "string") {
    return null;
  }

  const trimmed = target.trim();
  if (!trimmed.startsWith("/")) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, "http://localhost");
    if (isAuthRoutePath(parsed.pathname)) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function buildRouteTarget(locationLike?: LocationLike | null): string | null {
  if (!locationLike || typeof locationLike !== "object") {
    return null;
  }

  const pathname = normalizePathSegment(locationLike.pathname);
  if (!pathname.startsWith("/")) {
    return null;
  }

  const search = normalizePathSegment(locationLike.search);
  const hash = normalizePathSegment(locationLike.hash);
  return normalizeRouteTarget(`${pathname}${search}${hash}`);
}

export function readLastAuthenticatedRoute(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return normalizeRouteTarget(
    window.sessionStorage.getItem(LAST_AUTHENTICATED_ROUTE_STORAGE_KEY),
  );
}

export function writeLastAuthenticatedRoute(target: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = normalizeRouteTarget(target);
  if (!normalized) {
    return;
  }

  window.sessionStorage.setItem(
    LAST_AUTHENTICATED_ROUTE_STORAGE_KEY,
    normalized,
  );
}

export function clearLastAuthenticatedRoute(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(LAST_AUTHENTICATED_ROUTE_STORAGE_KEY);
}

export function resolveAuthenticatedRedirectTarget(
  locationState: unknown,
  fallback = "/dashboard",
): string {
  const stateObject =
    locationState && typeof locationState === "object"
      ? (locationState as { from?: LocationLike | null })
      : null;

  return (
    buildRouteTarget(stateObject?.from) ||
    readLastAuthenticatedRoute() ||
    fallback
  );
}